import { FeatureFlag } from '@ps/web-core-enums';
import type { TrackedTranslation, TranslationTag } from '@ps/web-core-intl';
import { useFeature } from '@ps/web-core-store';
import type { Error } from '@ps/web-shared-validators';
import { WHITE_START_OR_END_REGEXP } from '@ps/web-shared-validators';
import debounce from 'debounce-promise';
import type { FieldValidator } from 'final-form';
import { memoize } from 'lodash';
import React, { useRef } from 'react';
import { useIntl } from 'react-intl';

import type { BonusInfo } from '../../../../types/Cashier';
import { checkBonusData } from '../../../../utils/bonus';
import TextInput from '../../../TextInput/TextInput';

import {
  TXTSRV_Please_enter_a_valid_bonus_cod_ELL,
  TXTWCL_Bonus_code,
  TXTWCL_Your_code,
  TXTWCL_Your_code_cannot_start_or_end__ELL
} from './BonusInput.messages.json';

interface Props {
  selected?: boolean;
  label?: TrackedTranslation;
  placeholder?: TranslationTag;
  className?: string;
  checkBonusResponse?: boolean;
}

const fieldName = 'deposit.bonusCode';

const BonusInput: React.FC<Props> = ({
  selected,
  label = TXTWCL_Bonus_code,
  placeholder = TXTWCL_Your_code,
  className,
  checkBonusResponse
}) => {
  const intl = useIntl();
  const bonusResponses = useRef<{ [k: string]: BonusInfo }>(undefined);
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);

  const checkBonusCodeAsync = debounce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (bonusCode: string, allValues: any): Promise<Error> => {
      if (!bonusCode || !selected) {
        return { valid: true };
      }

      if (WHITE_START_OR_END_REGEXP.test(bonusCode)) {
        return {
          valid: false,
          message: [
            TXTSRV_Please_enter_a_valid_bonus_cod_ELL,
            TXTWCL_Your_code_cannot_start_or_end__ELL
          ]
        };
      }

      if (checkBonusResponse && bonusResponses.current && bonusResponses.current[bonusCode]) {
        return checkBonusData(bonusResponses.current[bonusCode], allValues);
      }

      return { valid: true };
    },
    500
  );

  const validateBonusCode: FieldValidator<string> = memoize(
    (bonusCode, allValues) => checkBonusCodeAsync(bonusCode, allValues),
    checkBonusResponse
      ? (bonusCode, allValues) => JSON.stringify([bonusCode, allValues])
      : undefined
  );

  return (
    <TextInput
      id="ownBonusCode"
      name={fieldName}
      label={label}
      autoComplete="off"
      placeholder={intl.formatMessage(placeholder)}
      validators={[validateBonusCode]}
      className={className}
      autoFocus={isAutofocusEnabled}
    />
  );
};

export default BonusInput;
